import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Dropdown } from '@/components/molecules/common/Form';
import { useTranslation } from 'react-i18next';
import { useItem, useSubmitShippingLabel } from '@/hooks/item';
import { Skeleton } from '@mui/material';
import { ItemSubmitShippingLabelModal } from '@/types/modal';
import { ErrorModal, StandardModal, NoFooterModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';
import { FlagId, ShippingTypeId } from '@/types/common';
import { CountryCode, CountryVcHubMap, VcHub, ShippingCarrier } from '@/shared/constants';

const SellerToBuyerId = 'seller_to_buyer';
const SellerToVcId = 'seller_to_vc';
const BuyerReturnToSellerId = 'buyer_return_to_seller';
const BuyerReturnToVcId = 'buyer_return_to_vc';

const ShipToVcIds: string[] = [SellerToVcId, BuyerReturnToVcId];

const isSupportedCountry = (countryIso2: string | undefined | null): boolean =>
  !!countryIso2 && Object.prototype.hasOwnProperty.call(CountryVcHubMap, countryIso2);

const resolveCarriers = (isDirectShipping: boolean, originCountry: string, type: string): string[] => {
  switch (originCountry) {
    case CountryCode.Sweden:
      return [ShippingCarrier.DHL, ShippingCarrier.UPS];
    case CountryCode.SouthKorea:
      return [ShippingCarrier.DHL, ShippingCarrier.Fedex];
    case CountryCode.Czechia:
      return [ShippingCarrier.DHL, ShippingCarrier.UPS];
    default:
      break;
  }

  if (isDirectShipping && originCountry === CountryCode.Australia) {
    return ShipToVcIds.includes(type) ? [ShippingCarrier.DHL] : [ShippingCarrier.CouriersPlease];
  }

  switch (CountryVcHubMap[originCountry]) {
    case VcHub.HK:
    case VcHub.UK:
    case VcHub.EU:
      return [ShippingCarrier.DHL];
    case VcHub.US:
      return [ShippingCarrier.Fedex];
    default:
      return [];
  }
};

const View: React.FC<{ modal: ItemSubmitShippingLabelModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const [carriers, setCarriers] = useState([
    {
      label: '',
      value: '',
    },
  ]);
  const [carrier, setCarrier] = useState('');
  const [countryNotSupport, setCountryNotSupport] = useState(false);
  const [types, setTypes] = useState<{ label: string; value: string }[]>();
  const { sellerShippingAddress } = item;
  const shipFromSellerSupported = isSupportedCountry(sellerShippingAddress?.countryIso2);
  const shipFromBuyerSupported = isSupportedCountry(item.buyerCountryIso);

  useEffect(() => {
    const routeList: { label: string; value: string }[] = [];
    if (item.shippingPlanId == ShippingTypeId.DIRECT) {
      if (shipFromSellerSupported) {
        routeList.push({
          label: t('modal.item_submit_shipping_label.type_ds_seller_to_buyer'),
          value: SellerToBuyerId,
        });
        routeList.push({
          label: t('modal.item_submit_shipping_label.type_ds_seller_to_vc'),
          value: SellerToVcId,
        });
      }

      if (shipFromBuyerSupported) {
        routeList.push({
          label: t('modal.item_submit_shipping_label.type_ds_buyer_return_to_seller'),
          value: BuyerReturnToSellerId,
        });
        routeList.push({
          label: t('modal.item_submit_shipping_label.type_ds_buyer_return_to_vc'),
          value: BuyerReturnToVcId,
        });
      }
    } else if (item.shippingPlanId == ShippingTypeId.STANDARD || item.inStock) {
      if (shipFromSellerSupported) {
        routeList.push({
          label: t('modal.item_submit_shipping_label.type_ss_seller_to_vc'),
          value: SellerToVcId,
        });
      }

      if (shipFromBuyerSupported) {
        routeList.push({
          label: t('modal.item_submit_shipping_label.type_ss_buyer_return_to_vc'),
          value: BuyerReturnToVcId,
        });
      }
    }
    setTypes(routeList);
  }, []);

  const [type, setType] = useState('');
  const [originCountry, setOriginCountry] = useState(sellerShippingAddress?.countryIso2 ?? '');

  const itemR = useItem(item.productId, false);
  const submitR = useSubmitShippingLabel(item.productId);

  useEffect(() => {
    const carrierNames = resolveCarriers(item.shippingPlanId == ShippingTypeId.DIRECT, originCountry, type);
    if (!carrierNames.length) {
      setCarriers([{ label: '', value: '' }]);
      setCarrier('');
      return;
    }
    setCarriers(carrierNames.map((name) => ({ label: name, value: name })));
    setCarrier((current) => (carrierNames.includes(current) ? current : carrierNames[0]));
  }, [originCountry, type]);

  useEffect(() => {
    switch (type) {
      case SellerToBuyerId:
      case SellerToVcId:
        setOriginCountry(sellerShippingAddress?.countryIso2 ?? '');
        break;
      case BuyerReturnToSellerId:
      case BuyerReturnToVcId:
        setOriginCountry(item.buyerCountryIso ?? '');
        break;
    }
  }, [type]);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  const onSubmit = () => {
    submitR.mutate({
      carrier: carrier,
      type: type,
    });
  };

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      if (submitR.data.link) {
        window.open(submitR.data.link, '_blank');
      }
    }
  }, [submitR.kind]);

  useEffect(() => {
    if (types && types.length) {
      let defaultType = types[0].value;
      const flags = item.flags ? item.flags.map(({ id }) => id) : [];
      if (flags.includes(FlagId.RETURN_TO_WAREHOUSE)) {
        defaultType = BuyerReturnToVcId;
      } else if (flags.includes(FlagId.RETURN_TO_SELLER) && item.shippingPlanId == ShippingTypeId.DIRECT) {
        defaultType = BuyerReturnToSellerId;
      }
      setType(defaultType);
      setCountryNotSupport(false);
    } else if (types) {
      setCountryNotSupport(true);
    }
  }, [types]);

  if (submitR.kind === 'Loading') {
    return (
      <NoFooterModal
        title={t('modal.item_submit_shipping_label.title')}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div className={styles.updateItemAttributeWait}>{t('modal.item_submit_shipping_label.waiting')}</div>
          </>
        }
        onClose={onClose}
        modalName="item_submit_shipping_label"
      />
    );
  }

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_submit_shipping_label.title')}
        error={submitR.error}
        onClose={onClose}
        containerStyle={{
          width: 400,
        }}
      />
    );
  }

  if (countryNotSupport && undefined !== types) {
    return (
      <ErrorModal
        title={t('modal.item_submit_shipping_label.title')}
        message={t('modal.item_submit_shipping_label.route_not_supported')}
        onClose={onClose}
        containerStyle={{
          width: 400,
        }}
      />
    );
  }

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
      <div
        className={clsx([styles.text1rem, styles.textGrey])}
        dangerouslySetInnerHTML={{
          __html: t('modal.item_submit_shipping_label.description'),
        }}
      />
      <Dropdown
        required={true}
        disabled={false}
        label={t('modal.item_submit_shipping_label.shipping_route')}
        value={type}
        onSelect={setType}
        items={types ?? []}
      />
      <Dropdown
        required={true}
        disabled={false}
        label={t('modal.item_submit_shipping_label.carrier')}
        value={carrier}
        onSelect={setCarrier}
        items={carriers}
      />
    </div>
  );

  return (
    <StandardModal
      title={t('modal.item_submit_shipping_label.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={t('common.submit')}
      submittable={carrier != '' && type != ''}
      body={body}
      containerStyle={{ maxWidth: 650 }}
      modalName="item_submit_shipping_label"
    />
  );
};

export default View;
